import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { DillButton } from '../../../general/Dill/DillButton';
import BankDetailsModal from './BankDetailsModal';
import { usePlaidLink } from 'react-plaid-link';
import type { PlaidLinkOnSuccess } from 'react-plaid-link';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { myFirebaseAuth } from '../../../../firebase/config';
import { endProcessProgress, startProcessProgress } from '../../../../redux/globalSlices/authSlice';
import { BASE_API_URL } from '../../../../utils/constants';
import { openDillToast } from '../../../../utils/helpers';
import { additionalConsentPlaid, getPlaidLinkToken } from '../../../../redux/services/authService';
import { BankReference, blockedBankReferenceEmailMessage } from '@dill/dill-shared/dist/src';
import ShowBankReferenceDetails from './ShowBankReferenceDetails';
import * as Sentry from '@sentry/react';
import { Bank, ShieldCheck, Lock, Warning } from 'phosphor-react';
import { COLORS } from '../../../../utils/colors';

interface Props {
  onSubmit: (
    data: BankReference[] | null | ((prev: BankReference[] | null) => BankReference[] | null)
  ) => void;
  bankDetails: BankReference[] | null;
  bankError: string | null;
  bankReferenceRequiredFields: string[];
  supplierName: string;
  checkRequired?: boolean;
  onlyShowPlaidOption?: boolean;
  // Domains a manual bank reference's contact email may not be at (see BankDetailsModal).
  blockedEmailDomains?: string[];
}

const AppBankReference: React.FC<Props> = ({
  onSubmit,
  bankDetails,
  bankError,
  bankReferenceRequiredFields,
  supplierName,
  checkRequired,
  onlyShowPlaidOption,
  blockedEmailDomains,
}) => {
  const dispatch = useAppDispatch();
  const [openBankDetailsModal, setOpenBankDetailsModal] = useState<boolean>(false);
  const [linkToken, setLinkToken] = useState<string | null>(null);

  const { plaidTokenDetails } = useAppSelector((state) => state.auth);

  const componentRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!componentRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          console.log('Component is visible → fetching Plaid link token');
          dispatch(getPlaidLinkToken({ isIncludeStatement: true }));
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(componentRef.current);

    return () => {
      observer.disconnect();
    };
  }, [dispatch]);

  const handleAddBankReference = (newRef: BankReference) => {
    onSubmit((prev) => [...(prev || []), newRef]);
  };

  const handleRemoveBankReference = (index: number) => {
    if (!bankDetails) return;
    onSubmit((prev) => {
      if (!prev) return null;
      const updated = prev.filter((_, i) => i !== index);
      return updated.length > 0 ? updated : null;
    });
  };

  const handlePlaidLinkSuccess: PlaidLinkOnSuccess = async (publicToken, metadata) => {
    const token = await myFirebaseAuth.currentUser?.getIdToken();
    if (!token) return null;

    dispatch(startProcessProgress('getPlaidPublicToken'));

    const accessTokenResponse = await axios.post(
      `${BASE_API_URL}/paymentsAPI/plaid/publicToken`,
      { accountId: metadata.accounts[0].id, publicToken },
      {
        method: 'POST',
        headers: {
          Authorization: 'Bearer ' + token,
        },
      }
    );

    dispatch(endProcessProgress('getPlaidPublicToken'));

    if (
      accessTokenResponse.data &&
      accessTokenResponse.data.success &&
      accessTokenResponse.data?.data?.accessToken
    ) {
      dispatch(
        additionalConsentPlaid({
          accessToken: accessTokenResponse.data?.data?.accessToken || '',
          products: ['statements'],
        })
      );
      handleAddBankReference({
        connectionType: 'plaid',
        bankDetails: {
          bankName: metadata.institution?.name || '',
          accessToken: accessTokenResponse.data.data.accessToken,
          accountNumber: metadata.accounts[0].mask,
          contactPerson: '',
          phoneNumber: '',
          address: '',
          city: '',
          state: '',
          zip: '',
        },
      });
      dispatch(getPlaidLinkToken({ isIncludeStatement: true }));
    } else {
      openDillToast({
        type: 'ERROR',
        message: 'Failed to get access token from Plaid. Please try again.',
      });
    }
  };

  const {
    open: openPlaidLink,
    ready: isPlaidLinkReady,
    error: plaidError,
  } = usePlaidLink({
    onSuccess: handlePlaidLinkSuccess,
    token: linkToken,
    onExit: (error, metadata) => {
      dispatch(getPlaidLinkToken({ isIncludeStatement: true }));
      Sentry.captureException(error);
      console.log('Plaid exited:', error, metadata);
    },
  });

  useEffect(() => {
    if (plaidError) {
      console.error('Plaid error:', plaidError);
      openDillToast({
        type: 'ERROR',
        message: `Failed to load plaid link: ${plaidError.message}`,
      });
    }
  }, [plaidError]);

  useEffect(() => {
    if (plaidTokenDetails && plaidTokenDetails.link_token) {
      setLinkToken(plaidTokenDetails.link_token);
    }
  }, [plaidTokenDetails]);

  const handleConnectUsingPlaid = () => {
    console.log('Connecting using Plaid...');
    openPlaidLink();
  };

  const handleBankNotInPlaid = () => {
    setOpenBankDetailsModal(true);
  };

  const handleBankDetailsSubmit = (details: {
    bankName: string;
    accountNumber: string;
    contactPerson: string;
    phoneNumber: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    email: string;
  }) => {
    handleAddBankReference({
      connectionType: 'manual',
      bankDetails: {
        bankName: details.bankName,
        accessToken: '',
        accountNumber: details.accountNumber,
        contactPerson: details.contactPerson,
        phoneNumber: details.phoneNumber,
        address: details.address,
        city: details.city,
        state: details.state,
        zip: details.zip,
        email: details.email,
      },
    });
    setOpenBankDetailsModal(false);
  };

  return (
    <div ref={componentRef} className="flex flex-col gap-4">
      <BankDetailsModal
        open={openBankDetailsModal}
        handleClose={() => setOpenBankDetailsModal(false)}
        onSubmit={handleBankDetailsSubmit}
        bankReferenceRequiredFields={bankReferenceRequiredFields}
        blockedEmailDomains={blockedEmailDomains}
        blockedEmailMessage={blockedBankReferenceEmailMessage(supplierName)}
      />

      {/* Info callout */}
      <div className="rounded-xl border GREY_200-BORDER GREY_50-BG p-4 flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <div
            className="flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #3762FB 0%, #1D85FF 100%)' }}>
            <Bank size={16} className="text-white" weight="fill" />
          </div>
          <p className="text-sm font-semibold GREY_900-CLR">Bank Verification via Plaid</p>
        </div>
        <p className="text-xs GREY_600-CLR leading-relaxed">
          Verifying your bank account with our partner Plaid allows{' '}
          <span className="font-semibold GREY_800-CLR">{supplierName}</span> to more quickly approve
          your credit application.
        </p>
        <div className="flex flex-col gap-2">
          <div className="flex items-start gap-2">
            <ShieldCheck
              size={14}
              className="flex-shrink-0 mt-0.5"
              color={COLORS.SUCCESS_600}
              weight="fill"
            />
            <p className="text-xs GREY_600-CLR leading-relaxed">
              You are granting {supplierName} read-only access to your historical bank balances,
              line of credit, and NSF fee history.
            </p>
          </div>
          <div className="flex items-start gap-2">
            <Lock
              size={14}
              className="flex-shrink-0 mt-0.5"
              color={COLORS.PRIMARY_500}
              weight="fill"
            />
            <p className="text-xs GREY_600-CLR leading-relaxed">
              You are NOT verifying your bank account for payment, and {supplierName} will NOT be
              able to withdraw funds.
            </p>
          </div>
        </div>
        {!onlyShowPlaidOption && (
          <p className="text-xs GREY_600-CLR">
            If your bank is not one of the 12,000+ institutions supported by Plaid, you can add a
            bank reference manually.
          </p>
        )}
      </div>

      {/* Bank details section */}
      <div className="flex flex-col gap-3">
        {!checkRequired && (
          <span className="self-start text-xs font-medium GREY_500-CLR bg-gray-100 px-2.5 py-0.5 rounded-full">
            Optional
          </span>
        )}

        {bankDetails && bankDetails.length > 0 ? (
          <>
            <ShowBankReferenceDetails
              bankReferences={bankDetails}
              onRemove={handleRemoveBankReference}
            />
            <div className="flex flex-wrap gap-2 mt-1">
              <DillButton
                variant="ghost-blue"
                onClick={() => onSubmit(null)}
                label="Clear All Bank Details"
              />
              {!onlyShowPlaidOption && (
                <DillButton
                  variant="secondary"
                  onClick={handleBankNotInPlaid}
                  label="Add Another Bank (Manual)"
                />
              )}
              {isPlaidLinkReady && (
                <DillButton onClick={handleConnectUsingPlaid} label="Add Another Bank (Plaid)" />
              )}
            </div>
          </>
        ) : (
          <div className="w-full flex flex-col items-center gap-3 p-6 border GREY_200-BORDER rounded-xl WHITE-BG">
            <div
              className="flex items-center justify-center w-12 h-12 rounded-full"
              style={{ background: 'linear-gradient(135deg, #EEF2FF 0%, #DBEAFE 100%)' }}>
              <Bank size={24} color={COLORS.PRIMARY_500} weight="fill" />
            </div>
            <p className="text-sm GREY_500-CLR text-center">No bank connected yet</p>
            <div className="flex flex-col items-center gap-2 w-full max-w-[260px]">
              {isPlaidLinkReady ? (
                <DillButton onClick={handleConnectUsingPlaid} label="Connect Using Plaid" />
              ) : (
                <div className="flex items-center gap-2 text-xs GREY_400-CLR">
                  <div className="w-3 h-3 rounded-full border-2 border-blue-400 border-t-transparent animate-spin" />
                  Loading Plaid...
                </div>
              )}
              {!onlyShowPlaidOption && (
                <DillButton
                  variant="secondary"
                  onClick={handleBankNotInPlaid}
                  label="Bank Not in Plaid"
                />
              )}
            </div>
          </div>
        )}

        {bankError && (
          <div className="flex items-center gap-2 text-xs ERROR_500-CLR ERROR_50-BG border ERROR_200-BORDER rounded-lg px-3 py-2">
            <Warning size={14} weight="fill" color={COLORS.ERROR_500} />
            <span>{bankError}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppBankReference;
